/**
 * ModuleTile — 1.422.0 Modules-tab concurrent tiling (P1).
 *
 * One open module = one tile: header (display name + focus affordance +
 * control icons) above the module's lazy-mounted SPA, each tile with its OWN
 * Suspense + ErrorBoundary + Provider so one module's crash or slow bundle
 * never takes down its neighbors. The mounting stack is exactly what
 * ModuleHostPane used to do for the single selected module — moved here
 * unchanged so per-module behavior is identical, just multiplied.
 *
 * Focus: clicking anywhere in the header focuses the tile (accent border via
 * data-focused + host CSS). Controls (P2, icons redesigned 1.423.0): four
 * colorized SVG snap previews (workspace frame with the target half filled —
 * the icon shows exactly which snap spot it produces) + maximize + close.
 */
import { Suspense } from 'react';
import type { ReactNode } from 'react';
import { ErrorBoundary } from '../ErrorBoundary';
import { MODULE_REGISTRY } from './moduleRegistry';
import type { ModuleDockEdge } from '@/stores/uiStore';

function PassThrough({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

function TileLoadingFallback({ moduleId }: { moduleId: string }) {
  return (
    <div
      data-testid={`module-tile-loading-${moduleId}`}
      className="flex-1 flex items-center justify-center bg-vibe-bg text-vibe-text-secondary text-sm"
    >
      Loading module…
    </div>
  );
}

function TileErrorFallback({ moduleId }: { moduleId: string }) {
  return (
    <div
      data-testid={`module-tile-error-${moduleId}`}
      className="flex-1 flex items-center justify-center bg-vibe-bg text-vibe-text-secondary text-sm"
    >
      Module failed to load — check the console for the lazy-import error.
    </div>
  );
}

export interface ModuleTileProps {
  moduleId: string;
  displayName: string;
  focused: boolean;
  docked: ModuleDockEdge | null;
  maximized: boolean;
  onFocus: () => void;
  onClose: () => void;
  onDock: (edge: ModuleDockEdge | null) => void;
  onToggleMaximize: () => void;
}

/** 1.423.0 snap-preview icons — each dock button renders a miniature
 * workspace (outer frame) with the half it will occupy filled in, so the icon
 * IS the snap spot. Each edge gets a distinct Tailwind hue; the SVG draws in
 * currentColor so active/hover/idle intensity is pure class state. Fill rects
 * inset 1px from the 16×12 frame (stroke at x/y 0.5, 15×11). */
const DOCK_CONTROLS: {
  edge: ModuleDockEdge;
  label: string;
  hue: string;
  fill: { x: number; y: number; w: number; h: number };
}[] = [
  { edge: 'left', label: 'Dock left', hue: 'text-sky-400', fill: { x: 1.5, y: 1.5, w: 6.5, h: 9 } },
  { edge: 'top', label: 'Dock top', hue: 'text-emerald-400', fill: { x: 1.5, y: 1.5, w: 13, h: 4.5 } },
  { edge: 'bottom', label: 'Dock bottom', hue: 'text-amber-400', fill: { x: 1.5, y: 6, w: 13, h: 4.5 } },
  { edge: 'right', label: 'Dock right', hue: 'text-violet-400', fill: { x: 8, y: 1.5, w: 6.5, h: 9 } },
];

const MAX_FILL = { x: 1.5, y: 1.5, w: 13, h: 9 };

/** Miniature-workspace snap preview: frame in a muted currentColor, the
 * target region solid. data-snap-edge/-fill are the test contract. */
function SnapPreviewIcon({
  edge,
  fill,
}: {
  edge: ModuleDockEdge | 'max';
  fill: { x: number; y: number; w: number; h: number };
}) {
  return (
    <svg
      data-snap-edge={edge}
      width="16"
      height="12"
      viewBox="0 0 16 12"
      aria-hidden="true"
      className="block"
    >
      <rect
        x="0.5"
        y="0.5"
        width="15"
        height="11"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeOpacity="0.45"
        strokeWidth="1"
      />
      <rect
        data-snap-fill="true"
        x={fill.x}
        y={fill.y}
        width={fill.w}
        height={fill.h}
        rx="1"
        fill="currentColor"
      />
    </svg>
  );
}

export function ModuleTile({
  moduleId,
  displayName,
  focused,
  docked,
  maximized,
  onFocus,
  onClose,
  onDock,
  onToggleMaximize,
}: ModuleTileProps) {
  const entry = MODULE_REGISTRY[moduleId];
  if (!entry) return null;
  const ModuleApp = entry.loader;
  const Provider = entry.Provider ?? PassThrough;

  return (
    <div
      data-testid={`module-tile-${moduleId}`}
      data-focused={focused ? 'true' : 'false'}
      className={`flex flex-col h-full w-full overflow-hidden border ${
        focused ? 'border-vibe-accent/60' : 'border-vibe-border'
      } bg-vibe-bg rounded-sm`}
      onMouseDownCapture={onFocus}
    >
      <div
        className="flex items-center gap-1 px-2 py-1 border-b border-vibe-border bg-vibe-bg-secondary text-xs select-none"
        data-testid={`module-tile-header-${moduleId}`}
      >
        <span className="text-vibe-text truncate flex-1">{displayName}</span>
        {DOCK_CONTROLS.map(({ edge, label, hue, fill }) => {
          const active = docked === edge;
          return (
            <button
              key={edge}
              type="button"
              title={active ? `Undock (${label.toLowerCase()} active)` : label}
              aria-pressed={active}
              data-testid={`module-tile-dock-${edge}-${moduleId}`}
              onClick={() => onDock(active ? null : edge)}
              className={`px-1 py-0.5 rounded ${hue} ${
                active
                  ? 'bg-vibe-bg-tertiary ring-1 ring-current'
                  : 'opacity-55 hover:opacity-100 hover:bg-vibe-bg-tertiary'
              }`}
            >
              <SnapPreviewIcon edge={edge} fill={fill} />
            </button>
          );
        })}
        <button
          type="button"
          title={maximized ? 'Restore' : 'Maximize'}
          aria-pressed={maximized}
          data-testid={`module-tile-maximize-${moduleId}`}
          onClick={onToggleMaximize}
          className={`px-1 py-0.5 rounded text-vibe-accent ${
            maximized
              ? 'bg-vibe-bg-tertiary ring-1 ring-current'
              : 'opacity-55 hover:opacity-100 hover:bg-vibe-bg-tertiary'
          }`}
        >
          <SnapPreviewIcon edge="max" fill={MAX_FILL} />
        </button>
        <button
          type="button"
          title="Close module"
          data-testid={`module-tile-close-${moduleId}`}
          onClick={onClose}
          className="px-1 rounded text-vibe-text-secondary hover:text-vibe-text hover:bg-vibe-bg-tertiary"
        >
          ✕
        </button>
      </div>
      <ErrorBoundary fallback={<TileErrorFallback moduleId={moduleId} />}>
        <Suspense fallback={<TileLoadingFallback moduleId={moduleId} />}>
          <Provider>
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden scrollbar-thin">
              <ModuleApp />
            </div>
          </Provider>
        </Suspense>
      </ErrorBoundary>
    </div>
  );
}
